// ═══════════════════════════════════════════════════════════
// BALL CLASS - The Star of the Show
// ═══════════════════════════════════════════════════════════

import { Vector2, Bounds, BallState } from './types.js';
import { Utils } from './Utils.js';
import { Theme, applyNeonGlow, clearNeonGlow } from './Theme.js';

interface TrailPoint {
    x: number;
    y: number;
    alpha: number;
}

export class Ball {
    position: Vector2;
    velocity: Vector2;
    size: number;
    speed: number;
    bounds: Bounds;

    // Ball state
    private state: BallState = BallState.MOVING;

    // Speed boost
    private baseSpeed: number;
    private boostActive: boolean = false;
    private boostTimer: number = 0;
    private boostDuration: number = 5; // 5 seconds
    private boostMultiplier: number = 1.8;

    // Aiming mechanic
    private savedVelocity: Vector2 = { x: 0, y: 0 };
    private aimAngle: number = 0; // Current oscillating angle
    private aimTime: number = 0; // Time elapsed during aiming
    private currentDirection: number = 0; // Direction when aiming started
    private readonly aimOscillationRange: number = Math.PI / 3; // 60 degrees in radians
    private aimOscillationSpeed: number = 1.2; // Oscillations per second (adaptive difficulty)

    // Visual effects
    private trail: TrailPoint[] = [];
    private maxTrailLength: number = 40; // Increased for better trail
    private squashX: number = 1.0;
    private squashY: number = 1.0;
    private rotation: number = 0;
    private hue: number = 180; // Starting hue (cyan)
    private energyPulse: number = 0; // For energy core pulsing animation

    constructor(bounds: Bounds, size: number, speed: number, aimOscillationSpeed: number = 1.2) {
        this.bounds = bounds;
        this.size = size;
        this.speed = speed;
        this.baseSpeed = speed;
        this.aimOscillationSpeed = aimOscillationSpeed;

        // Start at center with random direction
        this.position = {
            x: bounds.width / 2,
            y: bounds.height / 2
        };

        // Random initial velocity
        const angle = Utils.random(0, Math.PI * 2);
        this.velocity = {
            x: Math.cos(angle) * speed,
            y: Math.sin(angle) * speed
        };
    }

    update(deltaTime: number): void {
        if (this.state === BallState.AIMING) {
            // Update aiming oscillation
            this.updateAiming(deltaTime);
            // Don't update position or velocity while aiming
            return;
        }

        // Update boost timer
        if (this.boostActive) {
            this.boostTimer -= deltaTime;
            if (this.boostTimer <= 0) {
                this.deactivateBoost();
            }
        }

        // Update position
        this.position.x += this.velocity.x * deltaTime;
        this.position.y += this.velocity.y * deltaTime;

        // Update rotation based on velocity
        this.rotation += (this.velocity.x + this.velocity.y) * deltaTime * 0.01;

        // Update squash and stretch
        this.updateSquash(deltaTime);

        // Update trail
        this.updateTrail();

        // Update energy pulse for core animation
        this.energyPulse += deltaTime * Theme.ball.energyPulseSpeed;

        // Cycle hue for rainbow effect (faster when boosted)
        const hueSpeed = this.boostActive ? 120 : 60;
        this.hue = (this.hue + hueSpeed * deltaTime) % 360;
    }

    private updateSquash(deltaTime: number): void {
        // Calculate squash based on velocity magnitude
        const velocityMagnitude = Math.sqrt(
            this.velocity.x * this.velocity.x + this.velocity.y * this.velocity.y
        );
        const speedRatio = velocityMagnitude / this.speed;

        // Determine squash direction
        const velocityAngle = Math.atan2(this.velocity.y, this.velocity.x);
        const squashAmount = 0.2 * speedRatio;

        // Apply squash in direction of movement
        const targetSquashX = 1 + squashAmount * Math.abs(Math.cos(velocityAngle));
        const targetSquashY = 1 + squashAmount * Math.abs(Math.sin(velocityAngle));

        // Smooth lerp to target squash
        this.squashX = Utils.lerp(this.squashX, targetSquashX, deltaTime * 10);
        this.squashY = Utils.lerp(this.squashY, targetSquashY, deltaTime * 10);
    }

    private updateTrail(): void {
        // Add current position to trail
        this.trail.push({
            x: this.position.x,
            y: this.position.y,
            alpha: 1.0
        });

        // Limit trail length
        if (this.trail.length > this.maxTrailLength) {
            this.trail.shift();
        }

        // Update trail alpha
        for (let i = 0; i < this.trail.length; i++) {
            this.trail[i].alpha = i / this.trail.length;
        }
    }

    checkBoundaryCollision(): { collided: boolean; normal: Vector2 | null } {
        let collided = false;
        let normal: Vector2 | null = null;

        const halfSize = this.size / 2;

        // Left wall
        if (this.position.x - halfSize < 0) {
            this.position.x = halfSize;
            this.velocity.x = Math.abs(this.velocity.x);
            collided = true;
            normal = { x: 1, y: 0 };
            this.applyBounceSquash('horizontal');
        }

        // Right wall
        if (this.position.x + halfSize > this.bounds.width) {
            this.position.x = this.bounds.width - halfSize;
            this.velocity.x = -Math.abs(this.velocity.x);
            collided = true;
            normal = { x: -1, y: 0 };
            this.applyBounceSquash('horizontal');
        }

        // Top wall
        if (this.position.y - halfSize < 0) {
            this.position.y = halfSize;
            this.velocity.y = Math.abs(this.velocity.y);
            collided = true;
            normal = { x: 0, y: 1 };
            this.applyBounceSquash('vertical');
        }

        // Bottom wall
        if (this.position.y + halfSize > this.bounds.height) {
            this.position.y = this.bounds.height - halfSize;
            this.velocity.y = -Math.abs(this.velocity.y);
            collided = true;
            normal = { x: 0, y: -1 };
            this.applyBounceSquash('vertical');
        }

        return { collided, normal };
    }

    private applyBounceSquash(direction: 'horizontal' | 'vertical'): void {
        if (direction === 'horizontal') {
            this.squashX = 0.7;
            this.squashY = 1.3;
        } else {
            this.squashX = 1.3;
            this.squashY = 0.7;
        }
    }

    applyDirectionalBoost(targetDirection?: Vector2): void {
        // Apply a slight boost in a given direction or random if not specified
        const boostStrength = 100;

        if (targetDirection) {
            const normalized = Utils.normalize(targetDirection);
            this.velocity.x += normalized.x * boostStrength;
            this.velocity.y += normalized.y * boostStrength;
        } else {
            // Random slight angle change
            const currentAngle = Math.atan2(this.velocity.y, this.velocity.x);
            const angleChange = Utils.random(-0.3, 0.3);
            const newAngle = currentAngle + angleChange;

            const speed = Math.sqrt(this.velocity.x ** 2 + this.velocity.y ** 2);
            this.velocity.x = Math.cos(newAngle) * speed;
            this.velocity.y = Math.sin(newAngle) * speed;
        }

        // Clamp velocity to max speed
        const currentSpeed = Math.sqrt(this.velocity.x ** 2 + this.velocity.y ** 2);
        if (currentSpeed > this.speed * 1.5) {
            this.velocity.x = (this.velocity.x / currentSpeed) * this.speed * 1.5;
            this.velocity.y = (this.velocity.y / currentSpeed) * this.speed * 1.5;
        }
    }

    // ═══════════════════════════════════════════════════════════
    // GOLF-STYLE AIMING MECHANIC
    // ═══════════════════════════════════════════════════════════

    startAiming(): void {
        if (this.state === BallState.MOVING) {
            this.state = BallState.AIMING;

            // Save current velocity
            this.savedVelocity = { ...this.velocity };

            // Calculate current direction
            this.currentDirection = Math.atan2(this.velocity.y, this.velocity.x);

            // Pause the ball
            this.velocity = { x: 0, y: 0 };

            // Reset aim time
            this.aimTime = 0;
        }
    }

    private updateAiming(deltaTime: number): void {
        // Update aim time
        this.aimTime += deltaTime;

        // Calculate oscillating angle using sine wave
        // Oscillates between -aimOscillationRange and +aimOscillationRange
        this.aimAngle = Math.sin(this.aimTime * this.aimOscillationSpeed * Math.PI * 2) * this.aimOscillationRange;
    }

    release(): void {
        if (this.state === BallState.AIMING) {
            this.state = BallState.LAUNCHING;

            // Calculate new direction (current direction + oscillating offset)
            const newDirection = this.currentDirection + this.aimAngle;

            // Apply speed boost (1.2x normal speed)
            const boostMultiplier = 1.2;
            const boostedSpeed = this.speed * boostMultiplier;

            // Set new velocity
            this.velocity = {
                x: Math.cos(newDirection) * boostedSpeed,
                y: Math.sin(newDirection) * boostedSpeed
            };

            // Transition to moving state
            this.state = BallState.MOVING;
        }
    }

    setDirection(angleInRadians: number): void {
        this.velocity = {
            x: Math.cos(angleInRadians) * this.speed,
            y: Math.sin(angleInRadians) * this.speed
        };
    }

    getState(): BallState {
        return this.state;
    }

    isAiming(): boolean {
        return this.state === BallState.AIMING;
    }

    updateOscillationSpeed(speed: number): void {
        this.aimOscillationSpeed = speed;
    }

    // ═══════════════════════════════════════════════════════════
    // SPEED BOOST MECHANIC
    // ═══════════════════════════════════════════════════════════

    activateBoost(): void {
        if (!this.boostActive) {
            this.boostActive = true;
            this.boostTimer = this.boostDuration;
            this.speed = this.baseSpeed * this.boostMultiplier;

            // Apply boost to current velocity
            const currentAngle = Math.atan2(this.velocity.y, this.velocity.x);
            this.velocity.x = Math.cos(currentAngle) * this.speed;
            this.velocity.y = Math.sin(currentAngle) * this.speed;
        } else {
            // Refresh boost timer if already active
            this.boostTimer = this.boostDuration;
        }
    }

    private deactivateBoost(): void {
        this.boostActive = false;
        this.boostTimer = 0;
        this.speed = this.baseSpeed;

        // Adjust velocity back to base speed
        const currentAngle = Math.atan2(this.velocity.y, this.velocity.x);
        this.velocity.x = Math.cos(currentAngle) * this.speed;
        this.velocity.y = Math.sin(currentAngle) * this.speed;
    }

    isBoostActive(): boolean {
        return this.boostActive;
    }

    getBoostTimeRemaining(): number {
        return this.boostTimer;
    }

    render(ctx: CanvasRenderingContext2D): void {
        // Render trail (behind ball)
        this.renderTrail(ctx);

        // Render boost flame trail particles if active
        if (this.boostActive) {
            this.renderBoostFlameTrail(ctx);
        }

        // Render aim arrow if aiming
        if (this.state === BallState.AIMING) {
            this.renderAimArrow(ctx);
        }

        // Render main ball with squash and stretch
        ctx.save();
        ctx.translate(this.position.x, this.position.y);
        ctx.rotate(this.rotation);

        // Apply squash and stretch (enhanced)
        const squashAmount = this.boostActive ? 1.1 : 1.0;
        ctx.scale(this.squashX * squashAmount, this.squashY * squashAmount);

        // Draw multiple glow layers for neon effect
        this.renderNeonGlowLayers(ctx);

        // Draw boost aura if active (turbo nitro effect)
        if (this.boostActive) {
            this.renderBoostAura(ctx);
        }

        // Draw energy core (pulsing center)
        this.renderEnergyCore(ctx);

        // Draw main orb body
        const mainColor = this.boostActive ? Theme.ball.boostColor : Theme.ball.coreColor;
        ctx.fillStyle = mainColor;
        ctx.fillRect(-this.size / 2, -this.size / 2, this.size, this.size);

        // Draw glowing border
        const borderColor = this.boostActive ? Theme.ball.boostColor : Theme.colors.glowWhite;
        applyNeonGlow(ctx, borderColor, 10);
        ctx.strokeStyle = borderColor;
        ctx.lineWidth = this.boostActive ? 3 : 2;
        ctx.strokeRect(-this.size / 2, -this.size / 2, this.size, this.size);
        clearNeonGlow(ctx);

        ctx.restore();
    }

    /**
     * Render multiple glow rings for intense neon effect
     */
    private renderNeonGlowLayers(ctx: CanvasRenderingContext2D): void {
        const glowColor = this.boostActive ? Theme.ball.boostColor : Theme.ball.glowColor;

        // Parse hex color to RGB
        const r = parseInt(glowColor.slice(1, 3), 16);
        const g = parseInt(glowColor.slice(3, 5), 16);
        const b = parseInt(glowColor.slice(5, 7), 16);

        // Draw 3 glow layers with increasing radius
        for (let i = 0; i < Theme.ball.glowRings; i++) {
            const radius = this.size * (1 + i * 0.8);
            const intensity = 0.8 - (i * 0.25);

            const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, radius);
            gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${intensity})`);
            gradient.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, ${intensity * 0.5})`);
            gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

            ctx.fillStyle = gradient;
            ctx.fillRect(-radius, -radius, radius * 2, radius * 2);
        }
    }

    /**
     * Render pulsing energy core in center
     */
    private renderEnergyCore(ctx: CanvasRenderingContext2D): void {
        const pulseScale = 1 + Math.sin(this.energyPulse) * 0.15;
        const coreSize = this.size * Theme.ball.energyCoreSize * pulseScale;

        ctx.save();

        // Core glow
        const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, coreSize);
        gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
        gradient.addColorStop(0.5, Theme.ball.coreColor);
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

        ctx.fillStyle = gradient;
        ctx.fillRect(-coreSize, -coreSize, coreSize * 2, coreSize * 2);

        ctx.restore();
    }

    /**
     * Render boost aura effect (turbo nitro)
     */
    private renderBoostAura(ctx: CanvasRenderingContext2D): void {
        const auraRadius = this.size * 2.5;

        // Rotating aura effect
        const auraAngle = this.rotation * 2;
        ctx.save();
        ctx.rotate(auraAngle);

        // Draw flame-like aura
        const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, auraRadius);
        gradient.addColorStop(0, 'rgba(255, 255, 0, 0.4)');
        gradient.addColorStop(0.3, 'rgba(255, 165, 0, 0.3)');
        gradient.addColorStop(0.6, 'rgba(255, 0, 255, 0.2)');
        gradient.addColorStop(1, 'rgba(255, 0, 255, 0)');

        ctx.fillStyle = gradient;
        ctx.fillRect(-auraRadius, -auraRadius, auraRadius * 2, auraRadius * 2);

        ctx.restore();
    }

    /**
     * Render flame trail particles when boosted
     */
    private renderBoostFlameTrail(ctx: CanvasRenderingContext2D): void {
        // Sample points from trail for flame particles
        const step = Math.max(1, Math.floor(this.trail.length / Theme.ball.trailParticleCount));

        for (let i = 0; i < this.trail.length; i += step) {
            const point = this.trail[i];
            const progress = i / this.trail.length;

            // Randomly choose from flame colors
            const colorIndex = Math.floor(progress * Theme.ball.flameColors.length);
            const color = Theme.ball.flameColors[Math.min(colorIndex, Theme.ball.flameColors.length - 1)];

            const size = 8 * progress;
            const alpha = point.alpha * 0.6;

            ctx.save();
            ctx.globalAlpha = alpha;

            // Parse hex color
            const r = parseInt(color.slice(1, 3), 16);
            const g = parseInt(color.slice(3, 5), 16);
            const b = parseInt(color.slice(5, 7), 16);

            // Draw flame particle with glow
            const gradient = ctx.createRadialGradient(point.x, point.y, 0, point.x, point.y, size);
            gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 1)`);
            gradient.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, 0.6)`);
            gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

            ctx.fillStyle = gradient;
            ctx.fillRect(point.x - size, point.y - size, size * 2, size * 2);

            ctx.restore();
        }
    }

    private renderAimArrow(ctx: CanvasRenderingContext2D): void {
        ctx.save();
        ctx.translate(this.position.x, this.position.y);

        // Calculate the current aim direction
        const aimDirection = this.currentDirection + this.aimAngle;

        // Rotate to aim direction
        ctx.rotate(aimDirection);

        // Enhanced neon laser sightline
        const laserLength = Theme.gameplay.laserLength;
        const pulseIntensity = 0.6 + Math.abs(Math.sin(this.aimTime * 4)) * 0.4;

        // Draw laser glow layers
        for (let i = 0; i < 3; i++) {
            const width = (Theme.gameplay.laserWidth + i * 2) * pulseIntensity;
            const alpha = (0.8 - i * 0.2) * pulseIntensity;

            ctx.strokeStyle = Theme.gameplay.laserColor.replace(')', `, ${alpha})`).replace('rgb', 'rgba');
            ctx.lineWidth = width;
            applyNeonGlow(ctx, Theme.gameplay.laserColor, 15 - i * 5);

            ctx.beginPath();
            ctx.moveTo(0, 0);
            ctx.lineTo(laserLength, 0);
            ctx.stroke();

            clearNeonGlow(ctx);
        }

        // Draw predicted path dots
        const dotSpacing = laserLength / Theme.gameplay.pathDotCount;
        for (let i = 1; i <= Theme.gameplay.pathDotCount; i++) {
            const x = i * dotSpacing;
            const dotAlpha = pulseIntensity * (1 - i / Theme.gameplay.pathDotCount);

            ctx.fillStyle = Theme.gameplay.pathColor.replace(/[\d.]+\)$/, `${dotAlpha})`);
            ctx.fillRect(
                x - Theme.gameplay.pathDotSize / 2,
                -Theme.gameplay.pathDotSize / 2,
                Theme.gameplay.pathDotSize,
                Theme.gameplay.pathDotSize
            );
        }

        // Draw arrow head (neon style)
        const arrowHeadSize = 15;
        ctx.fillStyle = Theme.gameplay.laserColor;
        applyNeonGlow(ctx, Theme.gameplay.laserColor, 20);

        ctx.beginPath();
        ctx.moveTo(laserLength, 0);
        ctx.lineTo(laserLength - arrowHeadSize, -arrowHeadSize / 2);
        ctx.lineTo(laserLength - arrowHeadSize, arrowHeadSize / 2);
        ctx.closePath();
        ctx.fill();

        clearNeonGlow(ctx);

        ctx.restore();
    }

    private renderTrail(ctx: CanvasRenderingContext2D): void {
        for (let i = 0; i < this.trail.length - 1; i++) {
            const point = this.trail[i];
            const progress = i / this.trail.length;

            ctx.save();
            ctx.globalAlpha = point.alpha * 0.5; // Enhanced visibility

            const size = (this.size * point.alpha) / 1.5; // Larger trail

            // Use hot magenta for trail as per theme
            const trailColor = this.boostActive ? Theme.ball.boostColor : Theme.ball.trailColor;

            // Parse hex color
            const r = parseInt(trailColor.slice(1, 3), 16);
            const g = parseInt(trailColor.slice(3, 5), 16);
            const b = parseInt(trailColor.slice(5, 7), 16);

            // Draw with glow effect
            const gradient = ctx.createRadialGradient(point.x, point.y, 0, point.x, point.y, size);
            gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${point.alpha})`);
            gradient.addColorStop(0.6, `rgba(${r}, ${g}, ${b}, ${point.alpha * 0.5})`);
            gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

            ctx.fillStyle = gradient;
            ctx.fillRect(point.x - size, point.y - size, size * 2, size * 2);

            ctx.restore();
        }
    }

    reset(bounds: Bounds): void {
        this.bounds = bounds;
        this.position = {
            x: bounds.width / 2,
            y: bounds.height / 2
        };

        // Reset boost state
        this.boostActive = false;
        this.boostTimer = 0;
        this.speed = this.baseSpeed;

        // Random initial velocity
        const angle = Utils.random(0, Math.PI * 2);
        this.velocity = {
            x: Math.cos(angle) * this.speed,
            y: Math.sin(angle) * this.speed
        };

        this.trail = [];
        this.squashX = 1.0;
        this.squashY = 1.0;
        this.rotation = 0;

        // Reset aiming state
        this.state = BallState.MOVING;
        this.aimTime = 0;
        this.aimAngle = 0;
    }
}
